import type { MouseEvent, ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/Badge';
import { InfoHint } from '../../ui/InfoHint';
import { TableHead, TableHeader, TableRow } from '../../ui/Table';
import type { BaseColumnConfig, SortState } from './types';

function headerLabel<TRow>(col: BaseColumnConfig<TRow>): ReactNode {
  if (!col.comingSoon) return <span>{col.header}</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{col.header}</span>
      <Badge variant="soon">Soon</Badge>
    </span>
  );
}

type Props<TRow> = {
  columns: BaseColumnConfig<TRow>[];
  sort: SortState;
  onSortChange: (next: SortState) => void;
  testIdPrefix: string;
};

export function GridHeader<TRow>({ columns, sort, onSortChange, testIdPrefix }: Props<TRow>) {
  const handleClick = (columnId: string, e: MouseEvent) => {
    const existing = sort.find((p) => p.columnId === columnId);
    const others = sort.filter((p) => p.columnId !== columnId);
    const nextDirection: 'asc' | 'desc' | null = !existing
      ? 'asc'
      : existing.direction === 'asc'
        ? 'desc'
        : null;
    if (e.shiftKey) {
      onSortChange(
        nextDirection === null ? others : [...others, { columnId, direction: nextDirection }],
      );
    } else {
      onSortChange(nextDirection === null ? [] : [{ columnId, direction: nextDirection }]);
    }
  };

  return (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        {columns.map((col) => {
          const sortEntry = sort.find((p) => p.columnId === col.id);
          const ActiveIcon =
            sortEntry?.direction === 'asc'
              ? ArrowUp
              : sortEntry?.direction === 'desc'
                ? ArrowDown
                : null;
          return (
            <TableHead
              key={col.id}
              aria-sort={
                sortEntry?.direction === 'asc'
                  ? 'ascending'
                  : sortEntry?.direction === 'desc'
                    ? 'descending'
                    : 'none'
              }
              className={cn(
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
                col.width,
              )}
            >
              <span className="inline-flex items-center gap-1">
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={(e) => handleClick(col.id, e)}
                    className={cn(
                      'group inline-flex cursor-pointer select-none items-center gap-1.5 transition-colors',
                      sortEntry ? 'text-primary' : 'text-foreground hover:text-primary',
                    )}
                    aria-label={`Sort by ${col.header}`}
                    aria-pressed={Boolean(sortEntry)}
                    data-testid={`${testIdPrefix}-sort-${col.id}`}
                  >
                    {headerLabel(col)}
                    {ActiveIcon ? (
                      <ActiveIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <ArrowUpDown
                        className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-50"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                ) : (
                  headerLabel(col)
                )}
                {col.info && <InfoHint term={col.header}>{col.info}</InfoHint>}
              </span>
            </TableHead>
          );
        })}
      </TableRow>
    </TableHeader>
  );
}
